import {
    DescriptionEntry,
    SelectEntry,
    isSelectEntryEdited,
    TextFieldEntry,
    isTextFieldEntryEdited,
    ListGroup,
} from "@bpmn-io/properties-panel";
import { useService } from "bpmn-js-properties-panel";
import { is } from "bpmn-js/lib/util/ModelUtil";

// Import your custom list group entries.
import serviceRequestProps from "../request/RequestProperties";
import serviceResponseProps from "../response/ResponseProperties";

import { isReferenceVald, nextId } from "../utils";

const LOW_PRIORITY = 500;

/**
 * A provider with a `#getGroups(element)` method
 * that exposes groups for a diagram element.
 *
 * @param {PropertiesPanel} propertiesPanel
 * @param {Function} translate
 */
export default function ServicePropertiesProvider(
    propertiesPanel,
    injector,
    translate,
    moddle
) {
    /**
     * Return the groups provided for the given element.
     *
     * @param {DiagramElement} element
     *
     * @return {(Object[]) => (Object[])} groups middleware
     */
    this.getGroups = function (element) {
        /**
         * We return a middleware that modifies
         * the existing groups.
         *
         * @param {Object[]} groups
         *
         * @return {Object[]} modified groups
         */
        return function (groups) {
            if (is(element, "bpmn:ServiceTask")) {
                for (let group in extensions) {
                    if (extensions[group].services) {
                        groups.push(
                            createGroupExtension(
                                group,
                                extensions[group].name,
                                element,
                                injector,
                                translate
                            )
                        );
                    }
                }

                groups.push(createGroupService(element, injector, translate));
                // groups.push(createGroupPublish(element, injector, translate));
            }

            return groups;
        };
    };

    // Register our custom properties provider.
    // Use a lower priority to ensure it is loaded after
    // the basic BPMN properties.
    propertiesPanel.registerProvider(LOW_PRIORITY, this);
}

function createGroupExtension(id, name, element, injector, translate) {
    return {
        id: id,
        label: translate(name),
        entries: [
            {
                id: id,
                component: Extension,
                isEdited: isSelectEntryEdited,
                element,
            },
        ],
    };
}

function createGroupService(element, injector, translate) {
    return {
        id: "service-group",
        label: translate("Service"),
        entries: [
            {
                id: "serviceReference",
                component: ServiceReference,
                isEdited: isTextFieldEntryEdited,
            },
            {
                id: "serviceName",
                component: ServiceName,
                isEdited: isTextFieldEntryEdited,
            },
            {
                id: "request",
                label: translate("Request"),
                component: ListGroup,
                ...serviceRequestProps({ element, injector }),
            },
            {
                id: "response",
                label: translate("Response"),
                component: ListGroup,
                ...serviceResponseProps({ element, injector }),
            },
            {
                id: "description-footer",
                forId: "service-group",
                value: (
                    <div>
                        <label class="bio-properties-panel-description">
                            *Required fields
                        </label>
                        <p>
                            Request and response represent values for the
                            process store.
                        </p>
                        <p>
                            Publish a process service file to the
                            Brixx-Process-Engine.
                        </p>
                    </div>
                ),
                component: ServiceDescription,
            },
            {
                component: PublishServive,
            },
        ],
    };
}

function createGroupPublish(element, injector, translate) {
    return {
        id: "publish-group",
        label: translate("Publish"),
        entries: [
            {
                id: "description-footer",
                forId: "publish-group",
                value: (
                    <div>
                        <p>
                            Publish a process service file to the
                            Brixx-Process-Engine.
                        </p>
                    </div>
                ),
                component: PublishDescription,
            },
            {
                component: PublishServive,
            },
        ],
    };
}

function Extension(props) {
    const { id, element } = props;
    const modeling = useService("modeling");
    const translate = useService("translate");
    const debounce = useService("debounceInput");
    const types = [];

    const getValue = () => {
        let extension = undefined;

        if (extensions[id].services)
            for (let item of extensions[id].services) {
                if (item.id === element.businessObject.serviceReference) {
                    extension = true;
                    return element.businessObject.serviceReference;
                }
            }

        return extension;
    };

    const setValue = (value) => {
        let extension = undefined;
        let serviceName = "";
        
        if (extensions[id].services)
            for (let item of extensions[id].services) {
                if (item.id === value) {
                    extension = true;
                    serviceName = item.name;
                }
            }

        return modeling.updateProperties(element, {
            extension: extension,
            serviceReference: value || undefined,
            serviceName: serviceName,
        });
    };

    const getOptions = () => {
        const options = [];

        if (extensions[id].services)
            extensions[id].services.map((item) => {
                options.push({ label: translate(item.name), value: item.id });
            });

        return [
            { label: translate("<none>"), value: undefined },
            ...options.map((item) => item),
            ...types.map((type) => ({
                label: type,
                value: type,
            })),
        ];
    };

    const entry = (
        <SelectEntry
            id={id}
            element={element}
            label={translate("Extension")}
            getValue={getValue}
            setValue={setValue}
            getOptions={getOptions}
            debounce={debounce}
        />
    );

    return entry;
}

function ServiceReference(props) {
    const { element, id } = props;
    const modeling = useService("modeling");
    const translate = useService("translate");
    const debounce = useService("debounceInput");

    const getValue = () => {
        if (element.businessObject.serviceReference === undefined)
            setValue(nextId("Reference_"));

        return element.businessObject.serviceReference;
    };

    const setValue = (value) => {
        let extension = undefined;
        let serviceName = "";

        for (let group in extensions) {
            if (extensions[group].services)
                for (let item of extensions[group].services) {
                    if (item.id === value) {
                        extension = true;
                        serviceName = item.name;
                    }
                }
        }

        return modeling.updateProperties(element, {
            extension: extension,
            serviceReference: value,
            serviceName: serviceName,
        });
    };

    const validate = (value) => {
        return isReferenceVald(value, translate);
    };

    const entry = (
        <TextFieldEntry
            id={id}
            element={element}
            label={translate("*Reference")}
            getValue={getValue}
            setValue={setValue}
            debounce={debounce}
            validate={validate}
        />
    );

    return entry;
}

function ServiceName(props) {
    const { element, id } = props;
    const modeling = useService("modeling");
    const translate = useService("translate");
    const debounce = useService("debounceInput");

    const getValue = () => {
        return element.businessObject.serviceName || "";
    };

    const setValue = (value) => {
        return modeling.updateProperties(element, {
            serviceName: value,
        });
    };

    const entry = (
        <div class="bio-properties-panel-group">
            <TextFieldEntry
                id={id}
                element={element}
                label={translate("Name")}
                description={translate("An optional service name.")}
                getValue={getValue}
                setValue={setValue}
                debounce={debounce}
            />
        </div>
    );

    return entry;
}

function ServiceDescription(props) {
    const { element, forId, value } = props;

    const entry = (
        <div style="margin-left:12px; margin-right:30px">
            <DescriptionEntry element={element} forId={forId} value={value} />
        </div>
    );

    return entry;
}

function PublishServive(props) {
    const { element } = props;
    const className = publishEnabled ? "icon-publish" : "icon-publish-disabled";
    const fileName = `${element.businessObject.serviceReference}.json`;

    const entry = (
        <div>
            <form action="">
                <input
                    type="file"
                    name="file"
                    id="file-input"
                    class="file-input"
                />
            </form>
            <button
                class="button-publish-properties"
                onClick={() => publishProcessService(fileName)}
                title="Publish service to Brixx-Process-Engine"
            >
                <span class={className}></span>
            </button>
        </div>
    );

    return entry;
}

function PublishDescription(props) {
    const { element, forId, value } = props;
    const entry = (
        <div style="margin-left:12px; margin-right:30px">
            <DescriptionEntry element={element} forId={forId} value={value} />
        </div>
    );

    return entry;
}

ServicePropertiesProvider.$inject = [
    "propertiesPanel",
    "injector",
    "translate",
    "moddle",
];
